import React from 'react';
import { useTranslation } from 'react-i18next';
import SupportersLabel from '@wix/da-ds/pkg/Label/Supporters';
import AdoptableCountdown from '@wix/da-ds/pkg/Time/AdoptableCountdown';
import useAdoptableState from '../../../AdoptableInfo/useAdoptableState';
import Card from '../../lego/Card';
import CardHeading from '../../lego/CardHeading';
import Description from '../../lego/Description';
import BuyerButtons from './BuyerButtons';
import SellerButtons from './SellerButtons';
import Countdown from '../../lego/Countdown';
import MinimumOffer from '../../lego/MinimumOffer';
import MinOfferAndCountdown from '../../lego/MinOfferAndCountdown';
import OfferList from '../../lego/OfferList';
import Stack from '../../lego/Stack';
import TradeCount from '../../lego/TradeCount';
import ResaleIndicator from '../../lego/ResaleIndicator';
import { useExclusiveCategoryTranslation } from '../../hooks';
import s from './AdoptableForSale.scss';
function getDescriptionTranslation(t, { isSeller, canAdopt, earlyAccess }) {
    if (isSeller) {
        return t('sidebar.exclusives.description.seller');
    } else if (canAdopt) {
        return t('sidebar.exclusives.description.buyer');
    } else if (earlyAccess.isInEarlyAccess && !earlyAccess.isSupporter) {
        return t('sidebar.exclusives.description.earlyAccess.notSupporter');
    } else {
        return t('sidebar.exclusives.description.other');
    }
}
/**
 * An adoptable specific variant of our exclusive sidebar card. Used only when the exclusive is currently listed for sale.
 *
 * NOTE: This component is used for all exclusive types as of this comment's writing. There is no difference spec'd between
 * these cards. Because I don't believe this will stay the case, I architected this in a way to make it easy to make a brand-new
 * sidebar card component.
 *
 * If an exclusive category's card features start to diverge, make a new component instead of putting it in here.
 */ const AdoptableForSale = ({ deviation, className })=>{
    var _deviation_extended_adoptable, _deviation_extended;
    const { t } = useTranslation();
    const exclusiveCategoryName = useExclusiveCategoryTranslation(deviation);
    const adoptableState = useAdoptableState(deviation);
    const { isSeller, canBuyNow, canMakeNewOffer, canManageExistingOffer, canSeeMinOfferPrice, canSeeOfferCountdown, hasOffers, offersList, minOfferPrice, saleCount, offerCountdownEndTime, offerCountdownHasEnded, onOfferTimerEnd, canSeeEarlyAccessCountdown, earlyAccessCountdownHasEnded, earlyAccessCountdownEndTime, onEarlyAccessTimerEnd, earlyAccess } = adoptableState;
    return /*#__PURE__*/ React.createElement(Card, {
        badgeType: "adoptable",
        className: className
    }, /*#__PURE__*/ React.createElement(Stack, {
        gap: 16
    }, /*#__PURE__*/ React.createElement(CardHeading, null, exclusiveCategoryName), (canBuyNow || canMakeNewOffer || canManageExistingOffer || isSeller) && /*#__PURE__*/ React.createElement(Description, null, getDescriptionTranslation(t, adoptableState)), !!saleCount && /*#__PURE__*/ React.createElement(TradeCount, {
        className: s['trade-count'],
        deviation: deviation,
        count: saleCount
    }), !saleCount && /*#__PURE__*/ React.createElement(ResaleIndicator, {
        isResellable: !!((_deviation_extended = deviation.extended) === null || _deviation_extended === void 0 ? void 0 : (_deviation_extended_adoptable = _deviation_extended.adoptable) === null || _deviation_extended_adoptable === void 0 ? void 0 : _deviation_extended_adoptable.resaleAllowed)
    }), isSeller && canSeeEarlyAccessCountdown && // Just checking earlyAccessCountdownEndTime for typescript's sake. canSeeEarlyAccessCountdown should cover it.
    earlyAccessCountdownEndTime && /*#__PURE__*/ React.createElement("p", {
        className: s['early-access-supporter-callout']
    }, t('sidebar.exclusives.descriptionExtra.earlyAccess.isSupporter.withEarlyAccessTimer', {
        postProcess: [
            'reactPostprocessor',
            'featureBrancher'
        ],
        logo: /*#__PURE__*/ React.createElement(SupportersLabel, {
            className: s['supporters-label'],
            withLink: true
        }),
        timer: /*#__PURE__*/ React.createElement(AdoptableCountdown, {
            className: s['inline-countdown'],
            countToDate: earlyAccessCountdownEndTime,
            endedClassName: s['ended'],
            endedMessage: `00:00:00`,
            onEnd: onEarlyAccessTimerEnd
        })
    })), isSeller ? /*#__PURE__*/ React.createElement(SellerButtons, {
        deviation: deviation,
        adoptableState: adoptableState
    }) : /*#__PURE__*/ React.createElement(BuyerButtons, {
        deviation: deviation,
        adoptableState: adoptableState
    }), earlyAccess.isInEarlyAccess && !isSeller && !canSeeOfferCountdown && /*#__PURE__*/ React.createElement("p", {
        className: s['early-access-supporter-callout']
    }, t('sidebar.exclusives.descriptionExtra.earlyAccess.isSupporter.withEarlyAccessTimer', {
        postProcess: [
            'reactPostprocessor'
        ],
        logo: /*#__PURE__*/ React.createElement(SupportersLabel, {
            className: s['supporters-label'],
            withLink: true
        })
    })), (canSeeOfferCountdown || canSeeEarlyAccessCountdown) && offerCountdownEndTime !== undefined && canSeeMinOfferPrice && minOfferPrice > 0 && /*#__PURE__*/ React.createElement(MinOfferAndCountdown, {
        adoptableState: adoptableState
    }), !canSeeOfferCountdown && !canSeeEarlyAccessCountdown && canSeeMinOfferPrice && /*#__PURE__*/ React.createElement(MinimumOffer, {
        value: minOfferPrice
    }), /*#__PURE__*/ React.createElement(Stack, {
        gap: 8
    }, canSeeOfferCountdown && !canSeeMinOfferPrice && /*#__PURE__*/ React.createElement(Countdown, {
        hasEnded: offerCountdownHasEnded,
        countToDate: offerCountdownEndTime,
        onEnd: onOfferTimerEnd
    }), !isSeller && !canSeeOfferCountdown && !canSeeMinOfferPrice && canSeeEarlyAccessCountdown && // Just checking earlyAccessCountdownEndTime for typescript's sake. canSeeEarlyAccessCountdown should cover it.
    earlyAccessCountdownEndTime && /*#__PURE__*/ React.createElement(Countdown, {
        isEarlyAccess: true,
        hasEnded: earlyAccessCountdownHasEnded,
        countToDate: earlyAccessCountdownEndTime,
        onEnd: onEarlyAccessTimerEnd
    }), hasOffers && /*#__PURE__*/ React.createElement(OfferList, {
        deviation: deviation,
        offers: offersList
    }))));
};
AdoptableForSale.displayName = 'AdoptableForSale';
export default AdoptableForSale;

//# sourceMappingURL=AdoptableForSale.js.map